/**
 * The firm's lines: the SIMs in its gateway and its trunk, as the administrator keeps them.
 *
 * Saving a line is what makes its endpoint exist on the PBX, under the name typed here and
 * with the password typed here — the same two the gateway's own page is given. So this is a
 * list of actions, each saving when it is pressed, and the page's save bar does not count
 * them. The password is written to the PBX and kept nowhere else: the list says when one was
 * last written, the edit dialog takes a new one and leaves the stored one alone when left
 * blank.
 *
 * A gateway registered as one account is one row; a gateway whose SIMs each register as
 * their own account is one row per SIM, and that is what lets a call leave on a particular
 * SIM. The help text says which is which, because the difference is a setting on the
 * gateway and not anything this screen can tell.
 *
 * A provider's trunk the site writes in the PBX's own configuration is added with no
 * password: the API asks the PBX whether it defines the name, and lists it without writing
 * anything there. Such a line has no password and no registration to show, so its row says
 * where it is set up instead, and editing it offers neither box.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { describe } from '../errors';
import { forgetLineNames } from '../../telephony/lines';
import {
  Banner,
  Button,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Pill,
  Section,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from '../../ui';
import {
  linesApi,
  phoneLineKinds,
  type NewPhoneLine,
  type PhoneLine,
  type PhoneLineKind,
} from './lines';

const blank: NewPhoneLine = {
  name: '',
  label: '',
  number: '',
  kind: 'Sim',
  address: '',
  password: '',
  dialPrefix: '',
};

export function PhoneLinesSection({
  onLines,
}: {
  /** Told the list each time it loads, for the order outgoing calls take (#328). */
  onLines?: (lines: PhoneLine[]) => void;
} = {}) {
  const { t } = useTranslation();

  const [lines, setLines] = useState<PhoneLine[] | null>(null);

  /** The dialog: adding a new line, or editing one. Null when closed. */
  const [editing, setEditing] = useState<PhoneLine | 'new' | null>(null);
  const [form, setForm] = useState<NewPhoneLine>(blank);
  const [removing, setRemoving] = useState<PhoneLine | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  /** The first box, focused when the dialog opens — the native dialog would focus its Close. */
  const firstBox = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const listed = await linesApi.list();

      setLines(listed);
      onLines?.(listed);
    } catch (failure) {
      setError(describe(failure, t, 'settings.couldNotLoad'));
    }
  }, [t, onLines]);

  useEffect(() => {
    // eslint-disable-next-line react/set-state-in-effect -- fetching, not cascading
    void load();
  }, [load]);

  useEffect(() => {
    if (editing === null) return;

    // After showModal has done its own focusing, which it does on the same tick.
    const frame = requestAnimationFrame(() => firstBox.current?.focus());

    return () => cancelAnimationFrame(frame);
  }, [editing]);

  function open(line: PhoneLine | 'new') {
    setDialogError(null);
    setForm(
      line === 'new'
        ? blank
        : {
            name: line.name,
            label: line.label,
            number: line.number ?? '',
            kind: line.kind,
            address: line.address ?? '',
            password: '',
            dialPrefix: line.dialPrefix ?? '',
          },
    );
    setEditing(line);
  }

  function set<K extends keyof NewPhoneLine>(key: K, value: NewPhoneLine[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function act(action: () => Promise<void>, inDialog = false) {
    setBusy(true);
    setError(null);
    setDialogError(null);

    try {
      await action();
    } catch (failure) {
      (inDialog ? setDialogError : setError)(describe(failure, t, 'settings.couldNotSave'));
    } finally {
      setBusy(false);
    }
  }

  // No password is a line the PBX's own configuration defines; the API asks the PBX.
  const complete = form.label.trim() !== '' && (editing !== 'new' || form.name.trim() !== '');

  /** A line the PBX's own configuration defines: nothing of it is written from here. */
  const onPbx = editing !== null && editing !== 'new' && editing.inPbxConfiguration;

  // No <form> of its own: this section sits inside the page's, and a form cannot hold one.
  function save() {
    if (editing === null || !complete) return;

    void act(async () => {
      if (editing === 'new') {
        await linesApi.add({ ...form, name: form.name.trim() });
      } else {
        await linesApi.update(editing.id, {
          label: form.label,
          number: form.number,
          kind: form.kind,
          address: form.address,
          password: form.password,
          dialPrefix: form.dialPrefix,
        });
      }

      setEditing(null);
      forgetLineNames();
      await load();
    }, true);
  }

  return (
    <Section
      title={t('phoneLines.title')}
      description={t('phoneLines.help')}
      actions={
        <Button size="sm" disabled={busy} onClick={() => open('new')}>
          <Icon name="plus" small />
          {t('phoneLines.add')}
        </Button>
      }
    >
      {error !== null && (
        <div className="px-5 pt-3">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      {lines === null ? (
        error === null && <EmptyState>{t('app.loading')}</EmptyState>
      ) : lines.length === 0 ? (
        <EmptyState>{t('phoneLines.none')}</EmptyState>
      ) : (
        <Table tight>
          <thead>
            <Tr>
              <Th>{t('phoneLines.label')}</Th>
              <Th>{t('phoneLines.name')}</Th>
              <Th>{t('phoneLines.number')}</Th>
              <Th>{t('phoneLines.kind')}</Th>
              <Th>{t('phoneLines.address')}</Th>
              <Th>{t('phoneLines.dialPrefix')}</Th>
              <Th>{t('phoneLines.state')}</Th>
              <Th />
            </Tr>
          </thead>
          <tbody>
            {lines.map((line) => (
              <Tr key={line.id}>
                <Td wrap>{line.label}</Td>
                <Td>
                  <span className="font-mono">{line.name}</span>
                </Td>
                <Td>
                  {line.number === null ? (
                    <span className="text-ink-3">—</span>
                  ) : (
                    <span className="tabular-nums">{line.number}</span>
                  )}
                </Td>
                <Td>{t(`phoneLines.kinds.${line.kind}`)}</Td>
                <Td>
                  {line.address === null ? (
                    <span className="text-ink-3">{t('phoneLines.byName')}</span>
                  ) : (
                    <span className="tabular-nums">{line.address}</span>
                  )}
                </Td>
                <Td>
                  {line.dialPrefix === null ? (
                    <span className="text-ink-3">—</span>
                  ) : (
                    <span className="tabular-nums">{line.dialPrefix}</span>
                  )}
                </Td>
                <Td>
                  {line.inPbxConfiguration ? (
                    <Pill tone="neutral">{t('phoneLines.inPbxConfiguration')}</Pill>
                  ) : (
                    <Pill tone={line.registered ? 'ok' : 'neutral'} dot>
                      {line.registered ? t('phoneLines.registered') : t('phoneLines.notRegistered')}
                    </Pill>
                  )}
                </Td>
                <Td>
                  <span className="flex justify-end gap-1.5">
                    <Button size="sm" disabled={busy} onClick={() => open(line)}>
                      {t('phoneLines.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy}
                      onClick={() => setRemoving(line)}
                    >
                      {t('phoneLines.remove')}
                    </Button>
                  </span>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Dialog
        open={editing !== null}
        wide
        title={
          editing === 'new' || editing === null
            ? t('phoneLines.add')
            : t('phoneLines.editTitle', { label: editing.label })
        }
        onClose={() => setEditing(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setEditing(null)}>
              {t('fleet.cancel')}
            </Button>
            <Button variant="primary" disabled={busy || !complete} onClick={save}>
              {editing === 'new' ? t('phoneLines.add') : t('settings.save')}
            </Button>
          </>
        }
      >
        <div
          className="flex flex-col gap-3"
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.target instanceof HTMLTextAreaElement) return;
            // Enter saves the line rather than submitting the settings page around it.
            e.preventDefault();
            save();
          }}
        >
          {dialogError !== null && <Banner tone="danger">{dialogError}</Banner>}

          {onPbx && (
            <p className="text-secondary text-ink-2 m-0">{t('phoneLines.inPbxConfigurationHelp')}</p>
          )}

          <Field label={t('phoneLines.name')} help={t('phoneLines.nameHelp')}>
            <Input
              ref={editing === 'new' ? firstBox : undefined}
              value={form.name}
              maxLength={40}
              disabled={editing !== 'new'}
              placeholder="gsm-goip"
              className="font-mono"
              onChange={(e) => set('name', e.target.value)}
            />
          </Field>

          <Field label={t('phoneLines.label')} help={t('phoneLines.labelHelp')}>
            <Input
              ref={editing === 'new' ? undefined : firstBox}
              value={form.label}
              maxLength={80}
              onChange={(e) => set('label', e.target.value)}
            />
          </Field>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label={t('phoneLines.number')} help={t('phoneLines.numberHelp')}>
              <Input
                value={form.number}
                inputMode="tel"
                placeholder="0895018282"
                onChange={(e) => set('number', e.target.value)}
              />
            </Field>

            <Field label={t('phoneLines.kind')} help={t('phoneLines.kindHelp')}>
              <Select
                value={form.kind}
                onChange={(e) => set('kind', e.target.value as PhoneLineKind)}
              >
                {phoneLineKinds.map((kind) => (
                  <option key={kind} value={kind}>
                    {t(`phoneLines.kinds.${kind}`)}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {!onPbx && (
              <Field label={t('phoneLines.address')} help={t('phoneLines.addressHelp')}>
                <Input
                  value={form.address}
                  placeholder="192.168.0.50"
                  className="tabular-nums"
                  onChange={(e) => set('address', e.target.value)}
                />
              </Field>
            )}

            <Field label={t('phoneLines.dialPrefix')} help={t('phoneLines.dialPrefixHelp')}>
              <Input
                value={form.dialPrefix}
                placeholder="91"
                maxLength={16}
                className="tabular-nums"
                onChange={(e) => set('dialPrefix', e.target.value)}
              />
            </Field>
          </div>

          {!onPbx && (
            <Field
              label={t('phoneLines.password')}
              help={editing === 'new' ? t('phoneLines.passwordHelp') : t('phoneLines.passwordKeepHelp')}
            >
              <Input
                type="password"
                autoComplete="new-password"
                value={form.password}
                maxLength={64}
                onChange={(e) => set('password', e.target.value)}
              />
            </Field>
          )}
        </div>
      </Dialog>

      <Dialog
        open={removing !== null}
        title={t('phoneLines.remove')}
        onClose={() => setRemoving(null)}
        actions={
          <>
            <Button variant="quiet" onClick={() => setRemoving(null)}>
              {t('fleet.cancel')}
            </Button>
            <Button
              variant="danger"
              disabled={busy || removing === null}
              onClick={() =>
                void act(async () => {
                  if (removing === null) return;
                  await linesApi.remove(removing.id);
                  setRemoving(null);
                  forgetLineNames();
                  await load();
                })
              }
            >
              {t('phoneLines.remove')}
            </Button>
          </>
        }
      >
        <p className="m-0">
          {t(
            removing?.inPbxConfiguration === true
              ? 'phoneLines.removeFromListConfirm'
              : 'phoneLines.removeConfirm',
            {
              label: removing?.label ?? '',
              name: removing?.name ?? '',
            },
          )}
        </p>
      </Dialog>
    </Section>
  );
}
